import {
  type CreateFixedDepartureActivityRequest,
  type CreateFixedDepartureDepartureDateRequest,
  type UpdateFixedDepartureDepartureDateRequest,
  type FixedDepartureDepartureDateOut,
  FixedDepartureDepartureDateOutSchema,
  type CreateItineraryFromFixedDepartureRequest,
  type ReseedFixedDepartureItineraryRequest,
  type FixedDepartureQuotePreview,
  FixedDepartureQuotePreviewSchema,
  type FixedDepartureQuotePreviewQuery,
  type FixedDepartureRoomQuotePreviewRequest,
  type CreateFixedDepartureCategoryRequest,
  type CreateFixedDepartureVehicleSupplementRequest,
  type CreateFixedDepartureFlightRequest,
  type CreateFixedDepartureHotelRequest,
  type CreateFixedDepartureRequest,
  type CreateFixedDepartureTextRequest,
  type FixedDepartureActivityOut,
  FixedDepartureActivityOutSchema,
  type FixedDepartureCategoryOut,
  FixedDepartureCategoryOutSchema,
  type FixedDepartureVehicleSupplementOut,
  FixedDepartureVehicleSupplementOutSchema,
  type FixedDepartureDayOut,
  FixedDepartureDayOutSchema,
  type FixedDepartureDetail,
  FixedDepartureDetailSchema,
  type FixedDepartureExclusionOut,
  FixedDepartureExclusionOutSchema,
  type FixedDepartureFlightOut,
  FixedDepartureFlightOutSchema,
  type FixedDepartureHotelOut,
  FixedDepartureHotelOutSchema,
  type FixedDepartureInclusionOut,
  FixedDepartureInclusionOutSchema,
  type FixedDepartureLandPackageOut,
  FixedDepartureLandPackageOutSchema,
  type FixedDepartureListQuery,
  type FixedDepartureListResponse,
  FixedDepartureListResponseSchema,
  type FixedDepartureFacets,
  FixedDepartureFacetsSchema,
  type SaveFixedDepartureCategoryPricesRequest,
  type SaveFixedDepartureCitiesRequest,
  type SaveFixedDepartureLandPackageRequest,
  type UpdateFixedDepartureActivityRequest,
  type UpdateFixedDepartureCategoryRequest,
  type UpdateFixedDepartureVehicleSupplementRequest,
  type UpdateFixedDepartureDayRequest,
  type UpdateFixedDepartureFlightRequest,
  type UpdateFixedDepartureHotelRequest,
  type UpdateFixedDepartureRequest,
  type UpdateFixedDepartureTextRequest,
} from '@utazzo/shared';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

/** Fixed Departure template API. Every response validated against the shared zod schema. */

const base = (id: string) => `/fixed-departures/${id}`;

// ── Master ────────────────────────────────────────────────────────────────────

export const listFixedDepartures = async (
  query: FixedDepartureListQuery,
): Promise<FixedDepartureListResponse> => {
  const { data } = await apiClient.get<unknown>('/fixed-departures', { params: query });
  return FixedDepartureListResponseSchema.parse(data);
};

/**
 * Facets for the Country / City list filters - only the places templates
 * actually visit (a template stops in several cities, so this comes off the
 * itinerary city list, not one column).
 */
export const getFixedDepartureFacets = async (): Promise<FixedDepartureFacets> => {
  const { data } = await apiClient.get<unknown>('/fixed-departures/facets');
  return FixedDepartureFacetsSchema.parse(data);
};

export const getFixedDeparture = async (id: string): Promise<FixedDepartureDetail> => {
  const { data } = await apiClient.get<unknown>(base(id));
  return FixedDepartureDetailSchema.parse(data);
};

export const createFixedDeparture = async (
  payload: CreateFixedDepartureRequest,
): Promise<FixedDepartureDetail> => {
  const { data } = await apiClient.post<unknown>('/fixed-departures', payload);
  return FixedDepartureDetailSchema.parse(data);
};

export const updateFixedDeparture = async (
  id: string,
  payload: UpdateFixedDepartureRequest,
): Promise<FixedDepartureDetail> => {
  const { data } = await apiClient.patch<unknown>(base(id), payload);
  return FixedDepartureDetailSchema.parse(data);
};

export const deleteFixedDeparture = async (id: string): Promise<void> => {
  await apiClient.delete(base(id));
};

export const duplicateFixedDeparture = async (id: string): Promise<FixedDepartureDetail> => {
  const { data } = await apiClient.post<unknown>(`${base(id)}/duplicate`);
  return FixedDepartureDetailSchema.parse(data);
};

/** Replaces the city legs and regenerates day rows; returns the whole template. */
export const saveFixedDepartureCities = async (
  id: string,
  payload: SaveFixedDepartureCitiesRequest,
): Promise<FixedDepartureDetail> => {
  const { data } = await apiClient.put<unknown>(`${base(id)}/cities`, payload);
  return FixedDepartureDetailSchema.parse(data);
};

// ── Days (content only) ───────────────────────────────────────────────────────

export const updateFixedDepartureDay = async (
  id: string,
  dayId: string,
  payload: UpdateFixedDepartureDayRequest,
): Promise<FixedDepartureDayOut> => {
  const { data } = await apiClient.patch<unknown>(`${base(id)}/days/${dayId}`, payload);
  return FixedDepartureDayOutSchema.parse(data);
};

// ── Categories (grade tiers) + price grid ─────────────────────────────────────

export const createFixedDepartureCategory = async (
  id: string,
  payload: CreateFixedDepartureCategoryRequest,
): Promise<FixedDepartureCategoryOut> => {
  const { data } = await apiClient.post<unknown>(`${base(id)}/categories`, payload);
  return FixedDepartureCategoryOutSchema.parse(data);
};

export const updateFixedDepartureCategory = async (
  id: string,
  categoryId: string,
  payload: UpdateFixedDepartureCategoryRequest,
): Promise<FixedDepartureCategoryOut> => {
  const { data } = await apiClient.patch<unknown>(`${base(id)}/categories/${categoryId}`, payload);
  return FixedDepartureCategoryOutSchema.parse(data);
};

export const deleteFixedDepartureCategory = async (
  id: string,
  categoryId: string,
): Promise<void> => {
  await apiClient.delete(`${base(id)}/categories/${categoryId}`);
};

export const reorderFixedDepartureCategories = async (
  id: string,
  ids: string[],
): Promise<FixedDepartureCategoryOut[]> => {
  const { data } = await apiClient.put<unknown>(`${base(id)}/categories/reorder`, { ids });
  return z.array(FixedDepartureCategoryOutSchema).parse(data);
};

export const saveFixedDepartureCategoryPrices = async (
  id: string,
  categoryId: string,
  payload: SaveFixedDepartureCategoryPricesRequest,
): Promise<FixedDepartureCategoryOut> => {
  const { data } = await apiClient.put<unknown>(
    `${base(id)}/categories/${categoryId}/prices`,
    payload,
  );
  return FixedDepartureCategoryOutSchema.parse(data);
};

// ── Vehicle supplements ───────────────────────────────────────────────────────

export const createFixedDepartureVehicleSupplement = async (
  id: string,
  payload: CreateFixedDepartureVehicleSupplementRequest,
): Promise<FixedDepartureVehicleSupplementOut> => {
  const { data } = await apiClient.post<unknown>(`${base(id)}/vehicle-supplements`, payload);
  return FixedDepartureVehicleSupplementOutSchema.parse(data);
};

export const updateFixedDepartureVehicleSupplement = async (
  id: string,
  supplementId: string,
  payload: UpdateFixedDepartureVehicleSupplementRequest,
): Promise<FixedDepartureVehicleSupplementOut> => {
  const { data } = await apiClient.patch<unknown>(
    `${base(id)}/vehicle-supplements/${supplementId}`,
    payload,
  );
  return FixedDepartureVehicleSupplementOutSchema.parse(data);
};

export const deleteFixedDepartureVehicleSupplement = async (
  id: string,
  supplementId: string,
): Promise<void> => {
  await apiClient.delete(`${base(id)}/vehicle-supplements/${supplementId}`);
};

// ── Hotels ────────────────────────────────────────────────────────────────────

export const createFixedDepartureHotel = async (
  id: string,
  payload: CreateFixedDepartureHotelRequest,
): Promise<FixedDepartureHotelOut> => {
  const { data } = await apiClient.post<unknown>(`${base(id)}/hotels`, payload);
  return FixedDepartureHotelOutSchema.parse(data);
};

export const updateFixedDepartureHotel = async (
  id: string,
  hotelId: string,
  payload: UpdateFixedDepartureHotelRequest,
): Promise<FixedDepartureHotelOut> => {
  const { data } = await apiClient.patch<unknown>(`${base(id)}/hotels/${hotelId}`, payload);
  return FixedDepartureHotelOutSchema.parse(data);
};

export const deleteFixedDepartureHotel = async (id: string, hotelId: string): Promise<void> => {
  await apiClient.delete(`${base(id)}/hotels/${hotelId}`);
};

export const reorderFixedDepartureHotels = async (
  id: string,
  ids: string[],
): Promise<FixedDepartureHotelOut[]> => {
  const { data } = await apiClient.put<unknown>(`${base(id)}/hotels/reorder`, { ids });
  return z.array(FixedDepartureHotelOutSchema).parse(data);
};

// ── Land package ──────────────────────────────────────────────────────────────

export const saveFixedDepartureLandPackage = async (
  id: string,
  payload: SaveFixedDepartureLandPackageRequest,
): Promise<FixedDepartureLandPackageOut> => {
  const { data } = await apiClient.put<unknown>(`${base(id)}/land-package`, payload);
  return FixedDepartureLandPackageOutSchema.parse(data);
};

// ── Flights ───────────────────────────────────────────────────────────────────

export const createFixedDepartureFlight = async (
  id: string,
  payload: CreateFixedDepartureFlightRequest,
): Promise<FixedDepartureFlightOut> => {
  const { data } = await apiClient.post<unknown>(`${base(id)}/flights`, payload);
  return FixedDepartureFlightOutSchema.parse(data);
};

export const updateFixedDepartureFlight = async (
  id: string,
  flightId: string,
  payload: UpdateFixedDepartureFlightRequest,
): Promise<FixedDepartureFlightOut> => {
  const { data } = await apiClient.patch<unknown>(`${base(id)}/flights/${flightId}`, payload);
  return FixedDepartureFlightOutSchema.parse(data);
};

export const deleteFixedDepartureFlight = async (id: string, flightId: string): Promise<void> => {
  await apiClient.delete(`${base(id)}/flights/${flightId}`);
};

export const reorderFixedDepartureFlights = async (
  id: string,
  ids: string[],
): Promise<FixedDepartureFlightOut[]> => {
  const { data } = await apiClient.put<unknown>(`${base(id)}/flights/reorder`, { ids });
  return z.array(FixedDepartureFlightOutSchema).parse(data);
};

// ── Inclusions ────────────────────────────────────────────────────────────────

export const createFixedDepartureInclusion = async (
  id: string,
  payload: CreateFixedDepartureTextRequest,
): Promise<FixedDepartureInclusionOut> => {
  const { data } = await apiClient.post<unknown>(`${base(id)}/inclusions`, payload);
  return FixedDepartureInclusionOutSchema.parse(data);
};

export const updateFixedDepartureInclusion = async (
  id: string,
  inclusionId: string,
  payload: UpdateFixedDepartureTextRequest,
): Promise<FixedDepartureInclusionOut> => {
  const { data } = await apiClient.patch<unknown>(`${base(id)}/inclusions/${inclusionId}`, payload);
  return FixedDepartureInclusionOutSchema.parse(data);
};

export const deleteFixedDepartureInclusion = async (
  id: string,
  inclusionId: string,
): Promise<void> => {
  await apiClient.delete(`${base(id)}/inclusions/${inclusionId}`);
};

export const reorderFixedDepartureInclusions = async (
  id: string,
  ids: string[],
): Promise<FixedDepartureInclusionOut[]> => {
  const { data } = await apiClient.put<unknown>(`${base(id)}/inclusions/reorder`, { ids });
  return z.array(FixedDepartureInclusionOutSchema).parse(data);
};

// ── Exclusions ────────────────────────────────────────────────────────────────

export const createFixedDepartureExclusion = async (
  id: string,
  payload: CreateFixedDepartureTextRequest,
): Promise<FixedDepartureExclusionOut> => {
  const { data } = await apiClient.post<unknown>(`${base(id)}/exclusions`, payload);
  return FixedDepartureExclusionOutSchema.parse(data);
};

export const updateFixedDepartureExclusion = async (
  id: string,
  exclusionId: string,
  payload: UpdateFixedDepartureTextRequest,
): Promise<FixedDepartureExclusionOut> => {
  const { data } = await apiClient.patch<unknown>(`${base(id)}/exclusions/${exclusionId}`, payload);
  return FixedDepartureExclusionOutSchema.parse(data);
};

export const deleteFixedDepartureExclusion = async (
  id: string,
  exclusionId: string,
): Promise<void> => {
  await apiClient.delete(`${base(id)}/exclusions/${exclusionId}`);
};

export const reorderFixedDepartureExclusions = async (
  id: string,
  ids: string[],
): Promise<FixedDepartureExclusionOut[]> => {
  const { data } = await apiClient.put<unknown>(`${base(id)}/exclusions/reorder`, { ids });
  return z.array(FixedDepartureExclusionOutSchema).parse(data);
};

// ── Activities ────────────────────────────────────────────────────────────────

export const createFixedDepartureActivity = async (
  id: string,
  payload: CreateFixedDepartureActivityRequest,
): Promise<FixedDepartureActivityOut> => {
  const { data } = await apiClient.post<unknown>(`${base(id)}/activities`, payload);
  return FixedDepartureActivityOutSchema.parse(data);
};

export const updateFixedDepartureActivity = async (
  id: string,
  activityId: string,
  payload: UpdateFixedDepartureActivityRequest,
): Promise<FixedDepartureActivityOut> => {
  const { data } = await apiClient.patch<unknown>(`${base(id)}/activities/${activityId}`, payload);
  return FixedDepartureActivityOutSchema.parse(data);
};

export const deleteFixedDepartureActivity = async (
  id: string,
  activityId: string,
): Promise<void> => {
  await apiClient.delete(`${base(id)}/activities/${activityId}`);
};

export const reorderFixedDepartureActivities = async (
  id: string,
  ids: string[],
): Promise<FixedDepartureActivityOut[]> => {
  const { data } = await apiClient.put<unknown>(`${base(id)}/activities/reorder`, { ids });
  return z.array(FixedDepartureActivityOutSchema).parse(data);
};

// ── Creating an itinerary from a template (ADR-0069) ──────────────────────────

/** Dry-run the land price so the picker shows a real figure before committing. */
export const previewFixedDepartureQuote = async (
  id: string,
  query: FixedDepartureQuotePreviewQuery,
): Promise<FixedDepartureQuotePreview> => {
  const { data } = await apiClient.get<unknown>(`${base(id)}/quote-preview`, { params: query });
  return FixedDepartureQuotePreviewSchema.parse(data);
};

/** Dry-run the land price for a tier and a room configuration (per room). */
export const previewFixedDepartureRoomQuote = async (
  id: string,
  body: FixedDepartureRoomQuotePreviewRequest,
): Promise<FixedDepartureQuotePreview> => {
  const { data } = await apiClient.post<unknown>(`${base(id)}/quote-preview`, body);
  return FixedDepartureQuotePreviewSchema.parse(data);
};

export const createItineraryFromFixedDeparture = async (
  leadId: string,
  payload: CreateItineraryFromFixedDepartureRequest,
): Promise<{ id: string }> => {
  const { data } = await apiClient.post<{ id: string }>(
    `/leads/${leadId}/fixed-departure-itineraries`,
    payload,
  );
  return data;
};

/** Change tier / dates / party on an existing FD itinerary and re-seed it. */
export const reseedFixedDepartureItinerary = async (
  itineraryId: string,
  payload: ReseedFixedDepartureItineraryRequest,
): Promise<{ id: string }> => {
  const { data } = await apiClient.put<{ id: string }>(
    `/itineraries/${itineraryId}/fixed-departure-config`,
    payload,
  );
  return data;
};

// ── Departure dates ───────────────────────────────────────────────────────────

export const createFixedDepartureDepartureDate = async (
  id: string,
  payload: CreateFixedDepartureDepartureDateRequest,
): Promise<FixedDepartureDepartureDateOut> => {
  const { data } = await apiClient.post<unknown>(`${base(id)}/departure-dates`, payload);
  return FixedDepartureDepartureDateOutSchema.parse(data);
};

export const updateFixedDepartureDepartureDate = async (
  id: string,
  dateId: string,
  payload: UpdateFixedDepartureDepartureDateRequest,
): Promise<FixedDepartureDepartureDateOut> => {
  const { data } = await apiClient.patch<unknown>(`${base(id)}/departure-dates/${dateId}`, payload);
  return FixedDepartureDepartureDateOutSchema.parse(data);
};

export const deleteFixedDepartureDepartureDate = async (
  id: string,
  dateId: string,
): Promise<void> => {
  await apiClient.delete(`${base(id)}/departure-dates/${dateId}`);
};
